import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import uniqueId from 'lodash/uniqueId';

import {
  appendPartToMessages,
  ContentPart,
  Message,
  useInitializeChat,
  useSendMessage
} from '@/features/AIChat/context/utils';
import useUserDetails from '@/hooks/useUserDetails';

export interface ChatContextValue {
  messages: Message[];
  inputMessage: string;
  setInputMessage: React.Dispatch<React.SetStateAction<string>>;
  handleSend: () => void;
  sendMessage: (text: string) => void;
  isInitializing: boolean;
  isSending: boolean;
  setConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  conversationId: string | null;
  version: number;
}

export const ChatContext = createContext<ChatContextValue | undefined>(undefined);

export const ChatProvider = ({ open, children }: { open: boolean; children: React.ReactNode }) => {
  const { userEmail } = useUserDetails();

  const [conversationId, setConversationId] = useState<string | null>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const version = useRef(0);

  const initChat = useInitializeChat();

  const appendPart = useCallback((part: ContentPart) => {
    setMessages(prev => appendPartToMessages(prev, part));
  }, []);

  const { mutation: sendMutation, cancel: cancelSend } = useSendMessage(appendPart);

  useEffect(() => {
    if (open && conversationId === '' && !initChat.isPending) {
      setMessages([]);
      cancelSend();
      sendMutation.reset();
      initChat.mutate(undefined, {
        onSuccess: data => {
          setConversationId(data.conversation_id);
          version.current = data.version ? Number(data.version) : 0;
        },
        onError: error => {
          console.error('Error initializing chat:', error);
          setConversationId(null);
        }
      });
    }
  }, [open, conversationId, initChat, sendMutation, cancelSend]);

  const sendMessage = useCallback(
    (text: string) => {
      if (!conversationId || !text.trim()) return;

      setMessages(prev => [
        ...prev,
        {
          id: uniqueId(),
          sender: 'human',
          text,
          timestamp: new Date().toISOString()
        },
        {
          id: uniqueId(),
          sender: 'ai',
          timestamp: new Date().toISOString(),
          content: []
        }
      ]);

      sendMutation.mutate({
        conversationId,
        message: text,
        userId: userEmail || ''
      });
    },
    [conversationId, userEmail, sendMutation]
  );

  const handleSend = useCallback(() => {
    if (!inputMessage.trim()) return;
    sendMessage(inputMessage);
    setInputMessage('');
  }, [inputMessage, sendMessage]);

  useEffect(() => {
    // Cancel in-flight stream when conversation switches
    if (conversationId) {
      cancelSend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const value = useMemo<ChatContextValue>(
    () => ({
      messages,
      inputMessage,
      setInputMessage,
      handleSend,
      sendMessage,
      isInitializing: initChat.isPending,
      isSending: sendMutation.isPending,
      setConversationId,
      conversationId,
      version: version.current
    }),
    [
      messages,
      inputMessage,
      handleSend,
      sendMessage,
      initChat.isPending,
      sendMutation.isPending,
      conversationId
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
